/**
 * The escalation state machine.
 *
 * Written as a pure function over a dose snapshot and a clock, rather than as
 * methods on the Durable Object, for two reasons:
 *
 *  - A timer-driven distributed state machine is the class of system that is
 *    fine in QA and broken in production. Making it pure means a 30-day course
 *    can be simulated in milliseconds and the whole escalation trace asserted.
 *  - The device wants the same knowledge, to say "your son will be told in 12
 *    minutes" on the reminder screen. Same rules, one implementation.
 *
 * The DO owns the side effects: setting alarms, enqueuing notifications,
 * writing rows. This decides *what* should happen, never does it.
 */

import { MINUTE_MS } from './time.js';
import type { PhonePresence } from './phone-presence.js';

export type DoseStatus =
  | 'pending'
  | 'taken'
  | 'skipped'
  /** The alarm rang, or the phone was in touch, and nobody answered. We know. */
  | 'missed'
  /**
   * The window closed and we have no evidence either way — RX-SYNC-04.
   *
   * Not a softer `missed`. A DIFFERENT FACT. The phone had not been heard
   * from since before the dose was due, so it may have rung in a dead zone
   * and been taken, or never rung at all, and nothing here can tell those
   * apart. Writing `missed` in that state is the server asserting something
   * it does not know, and it is how a caregiver learns our alerts are wrong.
   *
   * FHIR has the same code for the same reason: `unknown` — "the state of
   * the medication use is not currently known" — distinct from `not-taken`.
   *
   * It is not terminal. When a queued answer finally arrives this becomes
   * `taken` or `skipped`, and the caregiver is told the truth then.
   */
  | 'unreported'
  | 'refused'
  /**
   * Replaced by the same slot at its new time — migration 0050.
   *
   * A meal time or a medicine's times changed while this dose was already
   * due and unanswered, so the reminder now belongs at the new time and a
   * new dose carries it (`moved_to_dose_id`). This one is closed: not due,
   * not missed, not an answer, never escalated, and left out of every count
   * — it is as if it had never been a dose. It exists so the change is on
   * record and so an answer sent to it (from a reminder already on a lock
   * screen) can be handed to the dose that replaced it.
   */
  | 'moved';

/** 0 = armed, nothing sent. 1 = late nudge sent. 2 = missed alert sent. */
export type EscalationStage = 0 | 1 | 2;

export type Urgency = 'low' | 'normal' | 'high' | 'critical';

export interface DoseSnapshot {
  id: string;
  dueAt: number;
  /** After this the dose can no longer be taken; it becomes `missed`. */
  windowEnd: number;
  status: DoseStatus;
  isCritical: boolean;
  /**
   * Whether a miss reaches anybody but the person — RX-REM-21.
   *
   * Optional, and absent means yes. Every dose that existed before this
   * flag escalated, and a default of "tell nobody" would silently stop
   * caregiver alerts for every medicine on every account — the one
   * failure the whole ladder exists to prevent.
   */
  escalates?: boolean;
  escalationStage: number;
  /**
   * When a caregiver tapped "I'll handle it" — RX-CARE-12.
   *
   * Someone has said they are dealing with this dose, so the *intermediate*
   * nudges stop: the other caregivers do not need to be told again that it is
   * running late, because a human has taken it on.
   *
   * It does NOT suppress the missed alert. If they had succeeded they would
   * have marked it taken; a claim that did not end in a taken dose is
   * precisely when everybody else needs to hear. Suppressing that would turn
   * one tap into a silent failure, which is the opposite of what the button
   * is for.
   */
  claimedAt?: number | null;
  /**
   * Whether the phone was in touch across this dose's window.
   *
   * Computed by the caller from `rangAt` and the device's last check-in —
   * see `phonePresence` — so this stays a pure function of a snapshot.
   *
   * **Required, deliberately.** A default either way has a silent failure
   * mode: defaulting to "heard" accuses people on no evidence, and
   * defaulting to "unknown" means a caller that forgets to pass it stops
   * telling caregivers about missed doses at all, for ever, with nothing
   * to show for it. The compiler asking the question is the only version
   * where forgetting is impossible — the same lesson as the full-screen
   * setting that had a helper and no caller.
   */
  presence: PhonePresence;
}

export interface EscalationPolicy {
  /** How long after `dueAt` before caregivers get the first nudge. */
  lateAfterMinutes: number;
  /** Tighter window for a medication marked critical. */
  criticalLateAfterMinutes: number;
  notifyOnLate: boolean;
  notifyOnMissed: boolean;
}

export const DEFAULT_ESCALATION_POLICY: EscalationPolicy = {
  lateAfterMinutes: 30,
  criticalLateAfterMinutes: 15,
  notifyOnLate: true,
  notifyOnMissed: true,
};

export type EscalationAction =
  | {
      type: 'notify';
      kind: 'dose_late' | 'dose_missed' | 'dose_unknown';
      doseEventId: string;
      urgency: Urgency;
    }
  | { type: 'mark_missed'; doseEventId: string }
  /** The window closed with nothing heard from the phone. See `unreported`. */
  | { type: 'mark_unreported'; doseEventId: string }
  /**
   * Ask the phone to check in, before deciding anything about it.
   *
   * Grace, then probe, then escalate — never escalate on first silence.
   * Monitoring systems converged on this independently: a stale check
   * triggers an ACTIVE CHECK rather than an alert, and the guidance for
   * device fleets is to wait and verify a device is still offline before
   * acting. A silent push that drains the outbox turns a large share of
   * "we cannot see her phone" into an answer, before anybody is told
   * anything.
   *
   * Best-effort and never load-bearing: delivery is not guaranteed, and
   * none arrives if the app was force-quit. It is a tie-breaker.
   */
  | { type: 'probe'; doseEventId: string }
  | { type: 'set_stage'; doseEventId: string; stage: EscalationStage }
  | { type: 'set_alarm'; at: number }
  /** This dose is finished with. Move to the next pending one. */
  | { type: 'advance' };

export interface StepResult {
  actions: EscalationAction[];
  /** The stage the dose should be at after these actions are applied. */
  stage: EscalationStage;
}

/** When caregivers should first hear about this dose being late. */
export function lateAt(dose: DoseSnapshot, policy: EscalationPolicy): number {
  const minutes = dose.isCritical
    ? policy.criticalLateAfterMinutes
    : policy.lateAfterMinutes;
  // A very short grace window must not push the nudge past the point where the
  // dose is already missed -- that would send "running late" after "missed".
  return Math.min(dose.dueAt + minutes * MINUTE_MS, dose.windowEnd);
}

/** The next instant the Durable Object needs to wake for this dose. */
export function nextCheckpoint(dose: DoseSnapshot, policy: EscalationPolicy): number | null {
  if (dose.status !== 'pending') return null;
  if (dose.escalationStage >= 2) return null;
  if (dose.escalationStage >= 1) return dose.windowEnd;
  return lateAt(dose, policy);
}

/**
 * Which pending dose the scheduler should wake for next — the one with the
 * EARLIEST checkpoint, not the one due first.
 *
 * Found in the B2C audit. The scheduler watched only the oldest pending dose,
 * and once that dose was "late" its next checkpoint was its window's end —
 * hours away for a medicine with a long window. A critical dose due in the
 * meantime waited behind it: its late nudge never went out and its missed
 * alert went out at the first dose's window end, up to ten hours late.
 */
export function pickNextDose<T extends DoseSnapshot>(
  doses: readonly T[],
  policy: EscalationPolicy,
): { dose: T; checkpoint: number } | null {
  let best: { dose: T; checkpoint: number } | null = null;
  for (const dose of doses) {
    const checkpoint = nextCheckpoint(dose, policy);
    if (checkpoint === null) continue;
    if (!best || checkpoint < best.checkpoint) best = { dose, checkpoint };
  }
  return best;
}

function urgencyFor(dose: DoseSnapshot): Urgency {
  return dose.isCritical ? 'critical' : 'high';
}

/**
 * Decide what to do about `dose` at `now`.
 *
 * Deliberately tolerant of waking late. A Durable Object alarm is delivered
 * best-effort-soon, not exactly, and the server may have been unreachable.
 * Waking an hour after the window closed must still produce the missed alert,
 * and must NOT first send a "running late" nudge that stopped being true.
 */
export function step(
  dose: DoseSnapshot,
  now: number,
  policy: EscalationPolicy = DEFAULT_ESCALATION_POLICY,
): StepResult {
  const stage = Math.min(Math.max(dose.escalationStage, 0), 2) as EscalationStage;

  // Acknowledged, skipped or already resolved: nothing left to escalate.
  if (dose.status !== 'pending') {
    return { actions: [{ type: 'advance' }], stage };
  }

  if (stage >= 2) {
    return { actions: [{ type: 'advance' }], stage: 2 };
  }

  /*
   * The window has closed. What that MEANS depends on whether we were in
   * touch — RX-SYNC-04.
   *
   * This wrote `missed` unconditionally, so a phone in a dead zone and a
   * phone sitting on a table ignored produced the same alert. They are
   * different facts, and the lone-worker field settled this long ago: the
   * only thing that promotes "unknown" to "confirmed" is a timed local
   * interrogation of the person that went unanswered. Our alarm is that
   * interrogation, and `rangAt` records whether it happened.
   *
   * So: heard from → the alarm asked and nobody answered → missed.
   * Not heard from → we have no evidence → unreported.
   */
  if (now >= dose.windowEnd) {
    const heard = dose.presence === 'rang' || dose.presence === 'online';
    const actions: EscalationAction[] = [
      heard
        ? { type: 'mark_missed', doseEventId: dose.id }
        : { type: 'mark_unreported', doseEventId: dose.id },
    ];
    /*
     * `escalates === false` means the person said this one reaches
     * nobody — a water reminder, not a heart tablet. The dose is still
     * MARKED missed, because the record is the record; it is only the
     * telling that stops. Marking and telling were the same decision
     * until now, and separating them is the whole point of RX-REM-21.
     */
    if (policy.notifyOnMissed && dose.escalates !== false) {
      actions.push({
        type: 'notify',
        kind: heard ? 'dose_missed' : 'dose_unknown',
        doseEventId: dose.id,
        /*
         * An unknown is never urgent. It cannot be: we do not know that
         * anything is wrong, and waking somebody at 2am to say so is how
         * the alerts that DO mean something stop being read.
         */
        urgency: heard ? urgencyFor(dose) : 'low',
      });
    }
    actions.push({ type: 'set_stage', doseEventId: dose.id, stage: 2 });
    actions.push({ type: 'advance' });
    return { actions, stage: 2 };
  }

  if (stage === 0) {
    const due = lateAt(dose, policy);
    if (now < due) {
      // Woke early. Re-arm rather than escalating prematurely.
      return { actions: [{ type: 'set_alarm', at: due }], stage: 0 };
    }
    const actions: EscalationAction[] = [];
    /*
     * Probe first, and independently of whether anyone is notified.
     *
     * This used to happen only as a side effect of sending the late
     * nudge, so a family who had turned late nudges off got no probe at
     * all and their dose went straight to `unreported` — the worst
     * outcome for exactly the people who had asked for less noise, not
     * less accuracy.
     *
     * Only when we cannot see the phone: there is nothing to ask a phone
     * that has already told us it rang.
     */
    if (dose.presence === 'offline' || dose.presence === 'unknown') {
      actions.push({ type: 'probe', doseEventId: dose.id });
    }
    // A claimed dose still advances through the ladder -- the stage must move
    // or the missed alert never fires -- but nobody is nudged about it.
    if (policy.notifyOnLate && !dose.claimedAt && dose.escalates !== false) {
      actions.push({
        type: 'notify',
        kind: 'dose_late',
        doseEventId: dose.id,
        urgency: 'normal',
      });
    }
    actions.push({ type: 'set_stage', doseEventId: dose.id, stage: 1 });
    actions.push({ type: 'set_alarm', at: dose.windowEnd });
    return { actions, stage: 1 };
  }

  // stage === 1, window still open: wait for it to close.
  return { actions: [{ type: 'set_alarm', at: dose.windowEnd }], stage: 1 };
}

/**
 * Snoozing pushes the DEVICE reminder. It deliberately does NOT move the
 * server's escalation clock.
 *
 * This is the behaviour the product exists for: a patient who snoozes four
 * times has not taken their medication, and their caregiver should still be
 * told at the same moment they would have been otherwise.
 */
export function snoozeChangesEscalation(): false {
  return false;
}
